import { useEffect, useRef, useState } from 'react';
import { getWsUrl } from '@/lib/api';

/**
 * useRealtime(channels: string[], onMessage: (msg) => void)
 * Subscribes to a list of channels via WebSocket and invokes onMessage with parsed payloads.
 * Returns { connected: boolean } so callers can render fallback polling indicators.
 */
export function useRealtime(channels, onMessage) {
  const wsRef = useRef(null);
  const channelsRef = useRef(channels);
  const onMessageRef = useRef(onMessage);
  const [connected, setConnected] = useState(false);
  channelsRef.current = channels;
  onMessageRef.current = onMessage;

  useEffect(() => {
    let stopped = false;
    let reconnectTimer = null;

    const connect = () => {
      if (stopped) return;
      try {
        const ws = new WebSocket(getWsUrl());
        wsRef.current = ws;
        ws.onopen = () => {
          setConnected(true);
          (channelsRef.current || []).forEach((c) => {
            try {
              ws.send(JSON.stringify({ action: 'subscribe', channel: c }));
            } catch (e) { /* noop */ }
          });
        };
        ws.onmessage = (evt) => {
          try {
            const payload = JSON.parse(evt.data);
            onMessageRef.current && onMessageRef.current(payload);
          } catch (e) { /* noop */ }
        };
        ws.onclose = () => {
          setConnected(false);
          if (!stopped) {
            reconnectTimer = setTimeout(connect, 2000);
          }
        };
        ws.onerror = () => {
          setConnected(false);
          try { ws.close(); } catch (e) { /* noop */ }
        };
      } catch (e) {
        reconnectTimer = setTimeout(connect, 2000);
      }
    };
    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current && wsRef.current.close(); } catch (e) { /* noop */ }
    };
  }, []);

  return { connected };
}

/**
 * usePolling(callback, intervalMs, deps, options): periodically invoke callback on interval.
 * Useful as a safety net alongside useRealtime.
 * Pass { immediate: true } to also run the callback right away on mount.
 */
export function usePolling(callback, intervalMs = 6000, deps = [], options = {}) {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const { immediate = false } = options;
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (immediate) {
      try { cbRef.current && cbRef.current(); } catch (e) { /* noop */ }
    }
    const id = setInterval(() => {
      try { cbRef.current && cbRef.current(); } catch (e) { /* noop */ }
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, immediate, ...deps]);
  /* eslint-enable react-hooks/exhaustive-deps */
}

import { formatMoney as _formatMoney } from '@/lib/policy';

export function fmtMoney(value, currency) {
  // Delegate to the policy-aware formatter so that changing the
  // currency in Admin -> Policies & Settings immediately flips every
  // existing fmtMoney() call in the app over to the new currency.
  return _formatMoney(value, currency);
}

export function fmtTimeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}
